import type { ItemFieldRow } from './item-field-reader';
import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerIntegerMetadata } from './from-base-player-integers';
import { constructPlayerThroughIntegersFromBase } from './from-base-player-integers';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createAbilityEssenceSourceCollector } from './ability-essence-sources';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { readCreaturePayloadStatuses, AuraOwnerGateUnavailable } from './creature-status-reader';
import { isAuraPassive } from './passive-integer-fold';
import { phpJsonString } from './php-json-values';

const forcedLayers = ['skill_is_rolled_at_max', 'skill_is_rolled_at_min'] as const;

/** The native pre-full-skills fold. Prior contributions are removed one copy
 * at a time so an authored/status duplicate survives a repeated evaluation. */
export function constructPlayerForcedSkillsFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata, input: FromBaseRow) {
    const host = fromBaseClone(input);
    const { readers, definition, deps } = createFromBasePlayerGrantContext(base, metadata, host, 'integer-rollup-full-skills');
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const rootEntries = (row: FromBaseRow, key: string) => entries({ [key]: row[key] }, key);
    const ids = (row: ItemFieldRow, key: string) => entries(row, key).map(readLayerListEntryUuid).filter(fromBaseUuid).map(id => id.toUpperCase());
    const computed = (host.computed ??= {}) as FromBaseRow;
    const cache = fromBaseRecord(computed._cache) ? computed._cache : {};
    const prior = fromBaseRecord(cache.forced_skill_rolls_from_passives) ? cache.forced_skill_rolls_from_passives : {};
    for (const key of forcedLayers) {
        const removed = ids(prior, key);
        if (!removed.length || !Object.prototype.hasOwnProperty.call(host, key)) continue;
        const root = rootEntries(host, key);
        for (const id of removed) {
            const index = root.findIndex(entry => readLayerListEntryUuid(entry).toUpperCase() === id);
            if (index >= 0) root.splice(index, 1);
        }
        if (root.length) host[key] = root;
        else delete host[key];
    }
    delete cache.forced_skill_rolls_from_passives;

    const conditions = createAbilityConditionEvaluator(deps);
    const gate = createAbilityEssenceSourceCollector({ ...deps, metadata: metadata.preliminary }).levelGate(host);
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: definition });
    const carriers = new Map<string, string>();
    const classIds = new Set<string>();
    for (let slot = 1; slot <= 20; slot++) for (const key of [`class_${slot}`, `subclass_${slot}`]) {
        const id = readLayerListEntryUuid(readers.layerValue(base.player_characters, key));
        if (fromBaseUuid(id)) classIds.add(id.toUpperCase());
    }
    for (const provider of providers.providers(host)) for (const container of ['feat', 'passive_ability'])
        for (const entry of entries(provider, container)) {
            const id = readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition }).toUpperCase();
            if (id && !carriers.has(id)) carriers.set(id, phpJsonString(readers.itemField(provider, 'item_id')));
        }
    const resolve = (entry: unknown): ItemFieldRow | null => {
        if (fromBaseRecord(entry) && fromBaseRecord(entry.item_arr)) return entry.item_arr;
        const id = readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition });
        return fromBaseUuid(id) ? definition(id) : null;
    };
    const hasForced = (row: ItemFieldRow) => forcedLayers.some(key => readers.layerValue(row, key) !== null);
    const allowed = (row: ItemFieldRow, source: string, entryId: string): boolean => {
        if (isAuraPassive(row, readers.layerValue)
            || (readers.layerValue(row, 'triggers_after_combat_begins') !== null && readers.layerValue(row, 'duration_type') !== null)
            || !conditions.check(row, host, false, false)) return false;
        const carrier = classIds.has(source.toUpperCase()) ? source : carriers.get(entryId.toUpperCase()) ?? '';
        return !gate || gate({ source: carrier }, row);
    };
    const added = new Map<string, Set<string>>();
    const take = (row: ItemFieldRow, source: string, entryId: string, parent: ItemFieldRow | null = null): void => {
        if ((parent && !allowed(parent, source, entryId)) || !allowed(row, source, entryId)) return;
        for (const key of forcedLayers) for (const id of ids(row, key)) {
            if (!added.has(key)) added.set(key, new Set());
            added.get(key)!.add(id);
        }
    };
    for (const container of ['passive_ability', 'feat']) for (const entry of entries(host, container)) {
        const row = resolve(entry);
        if (!row) continue;
        const source = fromBaseRecord(entry) ? phpJsonString(readers.layerValue(entry, 'source') ?? '') : '';
        const entryId = readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition });
        if (hasForced(row)) take(row, source, entryId);
        if (container === 'feat') for (const child of entries(row, 'passive_ability')) {
            const grant = resolve(child);
            if (grant && hasForced(grant)) take(grant, source, entryId, row);
        }
    }
    for (const [key, skillIds] of added) if (skillIds.size) {
        host[key] = [...rootEntries(host, key), ...skillIds];
        computed._cache = cache;
        cache.forced_skill_rolls_from_passives ??= {};
        (cache.forced_skill_rolls_from_passives as Record<string, string[]>)[key] = [...skillIds];
    }
    // Resolution deliberately reads only the root and payload status surfaces.
    // An authored provider layer does not become a forced roll before the fold.
    const statuses = Object.values(readCreaturePayloadStatuses(host, readers.layerValue, definition));
    const resolved = new Map<string, Set<string>>();
    for (const key of forcedLayers) resolved.set(key, new Set([
        ...rootEntries(host, key).map(readLayerListEntryUuid),
        ...statuses.flatMap(status => fromBaseRecord(status) && fromBaseRecord(status.status_arr) ? ids(status.status_arr, key) : []),
    ].map(id => id.toUpperCase())));
    const forced: Record<string, number | null> = {};
    for (const skill of metadata.skills) {
        const matches = (key: string): boolean => resolved.get(key)!.has(skill.item_id.toUpperCase());
        forced[skill.variable_name] = matches('skill_is_rolled_at_max') ? 20 : matches('skill_is_rolled_at_min') ? 1 : null;
    }
    return { row: host, forced };
}

/** The full-skills stage stays open until source walks, natural features and
 * derived skill abilities are constructed; no zero/default stands in for them. */
export function constructPlayerThroughForcedSkillsFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata) {
    const integers = constructPlayerThroughIntegersFromBase(base, metadata);
    if (integers.kind !== 'constructed-prefix') return integers;
    try {
        const result = constructPlayerForcedSkillsFromBase(base, metadata, integers.row);
        return { ...integers, ...result, completedStages: [...integers.completedStages, 'passive-forced-skill-rolls'] };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input' as const, stage: 'integer-rollup-full-skills', dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage' as const, stage: error.stage, detail: error.detail };
        if (error instanceof AuraOwnerGateUnavailable) return { kind: 'missing-stage' as const, stage: 'integer-rollup-full-skills', detail: error.reason };
        throw error;
    }
}
